// Change-request (maker-checker) client — employee master-data edits that a
// tenant's approval policy gates go through change requests instead of being
// applied directly. Also exposes the per-employee field change history and the
// tenant approval policy. All calls go via the gateway.
import { gatewayApi } from '@/lib/hrApi';

const BASE = '/api/v1';

// ---------------------------------------------------------------- types
export type ChangeRequestStatus = 'pending' | 'approved' | 'rejected' | 'cancelled';
export type ChangeRequestFilter = ChangeRequestStatus | 'all';

/** Old → new pair for one field inside a change request. */
export interface FieldChange {
  old: unknown;
  new: unknown;
}

export interface ChangeRequest {
  id: string;
  employee_id: string;
  changes: Record<string, FieldChange>;
  reason?: string | null;
  status: ChangeRequestStatus;
  requested_by?: string | null;
  decided_by?: string | null;
  decided_at?: string | null;
  decision_note?: string | null;
  created_at: string;
}

/** Shape PUT /employees/{id} returns (HTTP 202) when the edit needs approval. */
export interface PendingApprovalResponse {
  status: 'pending_approval';
  change_request_id: string;
  fields: string[];
  message?: string;
}

export interface ApprovalPolicy {
  enabled: boolean;
  /** Fields currently gated by approval. */
  fields: string[];
  /** All fields that CAN be gated (for the policy editor's checkbox list). */
  tracked_fields: string[];
  /** Effective set of fields only HR (not line managers) may approve. */
  hr_only_fields: string[];
  /** Statutory-identity fields — the default HR-only set (for editor grouping). */
  statutory_fields: string[];
  /** When false, EVERY change approval routes to HR (managers approve nothing). */
  manager_approves: boolean;
}

export interface ChangeHistoryEntry {
  id: string;
  field: string;
  old_value: string | null;
  new_value: string | null;
  effective_date?: string | null;
  reason?: string | null;
  changed_by?: string | null;
  source?: string | null;
  change_request_id?: string | null;
  created_at: string;
}

// ---------------------------------------------------------------- helpers
/**
 * Axios treats 202 as success, so a PUT /employees/{id} "result" may actually
 * be a pending-approval envelope instead of the updated employee. Detect it by
 * payload shape.
 */
export const isPendingApproval = (data: unknown): data is PendingApprovalResponse =>
  !!data
  && typeof data === 'object'
  && (data as PendingApprovalResponse).status === 'pending_approval'
  && Array.isArray((data as PendingApprovalResponse).fields);

/** 'contract_end_date' → 'Contract end date' (for field labels in UI). */
export const humanizeField = (field: string): string => {
  const label = field.replace(/_/g, ' ').trim();
  return label ? label.charAt(0).toUpperCase() + label.slice(1) : field;
};

// ---------------------------------------------------------------- change requests
export const listChangeRequests = (status: ChangeRequestFilter = 'pending') =>
  gatewayApi
    .get<{ requests: ChangeRequest[] }>(`${BASE}/change-requests`, { params: { status } })
    .then((r) => r.data.requests ?? []);

export const approveChangeRequest = (id: string, note?: string) =>
  gatewayApi
    .post<{ status: string; applied_fields: string[] }>(
      `${BASE}/change-requests/${id}/approve`,
      note?.trim() ? { note: note.trim() } : {},
    )
    .then((r) => r.data);

export const rejectChangeRequest = (id: string, note?: string) =>
  gatewayApi
    .post(`${BASE}/change-requests/${id}/reject`, note?.trim() ? { note: note.trim() } : {})
    .then((r) => r.data);

export const cancelChangeRequest = (id: string) =>
  gatewayApi.post(`${BASE}/change-requests/${id}/cancel`, {}).then((r) => r.data);

// ---------------------------------------------------------------- policy
export const getApprovalPolicy = () =>
  gatewayApi.get<ApprovalPolicy>(`${BASE}/change-requests/policy/current`).then((r) => r.data);

export const updateApprovalPolicy = (body: {
  enabled: boolean;
  fields: string[];
  hr_only_fields?: string[];
  manager_approves?: boolean;
}) =>
  gatewayApi.put<ApprovalPolicy>(`${BASE}/change-requests/policy/current`, body).then((r) => r.data);

// ---------------------------------------------------------------- change history
export const getEmployeeChangeHistory = (employeeId: string, field?: string) =>
  gatewayApi
    .get<{ history: ChangeHistoryEntry[] }>(`${BASE}/employees/${employeeId}/change-history`, {
      params: field ? { field } : {},
    })
    .then((r) => r.data.history ?? []);
